<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useResizeObserver } from '@vueuse/core';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import DashboardAppSplitDivider from './DashboardAppSplitDivider.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';

const DEFAULT_SPLIT_RATIO = 0.45;

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    DashboardAppSplitDivider,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['dashboardAppActiveChange'],
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    return { uiSettings, updateUISettings };
  },
  data() {
    return {
      activeIndex: 0,
      splitContainerWidth: 0,
      dragRatio: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    isDashboardAppActive() {
      return this.activeIndex > 0 && !!this.currentChat.id;
    },
    splitRatio() {
      if (this.dragRatio !== null) {
        return this.dragRatio;
      }
      const { dashboard_app_split_ratio: ratio } = this.uiSettings;
      return ratio || DEFAULT_SPLIT_RATIO;
    },
    messagesPaneStyle() {
      if (!this.isDashboardAppActive) {
        return { width: '100%' };
      }
      return { width: `${this.splitRatio * 100}%` };
    },
    appPaneStyle() {
      if (!this.isDashboardAppActive) {
        return { width: '0%' };
      }
      return { width: `${(1 - this.splitRatio) * 100}%` };
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', {
            inboxIds: [inboxId],
            includeAgentBots: true,
          });
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
    isDashboardAppActive(value) {
      this.$emit('dashboardAppActiveChange', value);
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    useResizeObserver(this.$refs.splitContainer, entries => {
      this.splitContainerWidth = entries[0]?.contentRect?.width || 0;
    });
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    onSplitRatioChange(ratio) {
      this.dragRatio = ratio;
    },
    onSplitResizeEnd(ratio) {
      this.dragRatio = null;
      this.updateUISettings({ dashboard_app_split_ratio: ratio });
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div ref="splitContainer" class="flex h-full min-h-0 m-0">
      <div class="flex h-full min-h-0 min-w-0" :style="messagesPaneStyle">
        <MessagesView
          v-if="currentChat.id"
          :inbox-id="inboxId"
          :is-inbox-view="isInboxView"
        />
        <EmptyState
          v-if="!currentChat.id && !isInboxView"
          :is-on-expanded-layout="isOnExpandedLayout"
        />
        <slot />
      </div>
      <DashboardAppSplitDivider
        v-if="isDashboardAppActive"
        :container-width="splitContainerWidth"
        :model-value="splitRatio"
        @update:model-value="onSplitRatioChange"
        @resize-end="onSplitResizeEnd"
      />
      <div
        v-if="dashboardApps.length"
        v-show="isDashboardAppActive"
        class="flex h-full min-h-0 min-w-0"
        :style="appPaneStyle"
      >
        <DashboardAppFrame
          v-for="(dashboardApp, index) in dashboardApps"
          v-show="activeIndex - 1 === index"
          :key="currentChat.id + '-' + dashboardApp.id"
          :is-visible="activeIndex - 1 === index"
          :config="dashboardApps[index].content"
          :position="index"
          :current-chat="currentChat"
        />
      </div>
    </div>
  </div>
</template>
